import api from "../../utils/axios";
import { CLIENT_REPORTS_ENDPOINTS } from "./endpoints";
import {
	ClientOrderActivity,
	ClientOrderActivityQuery,
	ClientsStoppedOrderingReport,
	ClientsWithoutOrdersReport,
	StoppedOrderingQuery,
	WithoutOrdersQuery,
} from "./types";

/**
 * Dates go on the wire as local wall-clock "YYYY-MM-DD" with NO timezone suffix.
 *
 * The API normalises a UTC-suffixed value by converting it to server-local time, so an ISO
 * "…Z" string shifts the window by the server offset and silently returns the wrong day. Every
 * date field on these endpoints is a calendar day, never an instant — same rule the Duplicate
 * Payments search follows.
 */
export type WireDate = string;

/**
 * Strips empty values so the API sees an absent parameter rather than "" — `lastOrderFrom=`
 * fails model binding, while omitting it applies the documented default.
 */
const cleanParams = <T extends object>(query: T): Record<string, unknown> =>
	Object.fromEntries(
		Object.entries(query).filter(
			([, value]) => value !== undefined && value !== null && value !== ""
		)
	);

export const getClientsStoppedOrdering = async (
	query: StoppedOrderingQuery
): Promise<ClientsStoppedOrderingReport> => {
	const response = await api.get<ClientsStoppedOrderingReport>(
		CLIENT_REPORTS_ENDPOINTS.stoppedOrdering,
		{ params: cleanParams(query) }
	);
	return response.data;
};

export const getClientsWithoutOrders = async (
	query: WithoutOrdersQuery
): Promise<ClientsWithoutOrdersReport> => {
	const response = await api.get<ClientsWithoutOrdersReport>(
		CLIENT_REPORTS_ENDPOINTS.withoutOrders,
		{ params: cleanParams(query) }
	);
	return response.data;
};

export const getClientOrderActivity = async (
	query: ClientOrderActivityQuery
): Promise<ClientOrderActivity> => {
	const response = await api.get<ClientOrderActivity>(
		CLIENT_REPORTS_ENDPOINTS.clientOrderActivity,
		{ params: cleanParams(query) }
	);
	return response.data;
};

// ── Excel exports ───────────────────────────────────────────────────────────────────────────

/**
 * The API builds the .xlsx itself — styled, Albanian headers, real date cells. So these download
 * the file rather than re-generating it from the rows on screen, which is also why they must not
 * go through exportDataToCsv (which drops valueFormatter output anyway).
 *
 * `paginationDisabled` is forced server-side on the export routes, so the file always holds every
 * matching row and never just the page being viewed.
 */
const downloadExport = async (
	endpoint: string,
	query: object
): Promise<Blob> => {
	const response = await api.get<Blob>(endpoint, {
		params: cleanParams(query),
		responseType: "blob",
	});
	return response.data;
};

export const exportClientsStoppedOrdering = (query: StoppedOrderingQuery) =>
	downloadExport(CLIENT_REPORTS_ENDPOINTS.stoppedOrderingExport, query);

export const exportClientsWithoutOrders = (query: WithoutOrdersQuery) =>
	downloadExport(CLIENT_REPORTS_ENDPOINTS.withoutOrdersExport, query);

/**
 * Reads the API's error message out of a failed blob request.
 *
 * With `responseType: "blob"` a 400 body arrives as a Blob, so the axios interceptor cannot see
 * `{ message }` inside it and falls back to a generic toast. Callers pass the error here to
 * recover the real reason — e.g. the 50 000-row cap, or a rejected sort key.
 */
export const readBlobErrorMessage = async (
	error: unknown
): Promise<string | null> => {
	const data = (error as { response?: { data?: unknown } })?.response?.data;
	if (!(data instanceof Blob)) {
		return null;
	}
	try {
		const text = await data.text();
		const parsed = JSON.parse(text) as { message?: string };
		return parsed?.message ?? null;
	} catch {
		return null;
	}
};

/** Hands the downloaded workbook to the browser under a readable Albanian filename. */
export const saveWorkbook = (blob: Blob, fileName: string) => {
	const url = window.URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.download = fileName;
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	window.URL.revokeObjectURL(url);
};
